/**
 * Off-origin link normalisation.
 *
 * Absolute `https://ocs.ca/...` URLs are everywhere in merchant data — section
 * settings across the landing-page templates, and Shopify navigation linklists,
 * which are not in this repository at all. Liquid contributes its own: `blog.url`
 * renders the store's canonical host regardless of which domain the visitor is
 * actually on.
 *
 * On production that is harmless, because the canonical host *is* the current
 * host. Anywhere else — a development store, a preview theme served from a
 * `.myshopify.com` domain — following one of those links throws the visitor onto
 * production and drops their session. This module rewrites them back onto the
 * origin the page is being served from.
 *
 * It is inert on production by design: `init` returns immediately when the
 * current host is one of PRODUCTION_HOSTS, attaching nothing.
 *
 * Two modules already solve a narrow slice of this by hand —
 * assets/faq-load-more.js and assets/faq-topic-selector.js each force a URL onto
 * `window.location.host` before using it. Both remain in place; they are
 * redundant once this ships, but retiring them is a separate change.
 *
 * Coverage is a sweep at DOMContentLoaded plus delegated capture-phase handlers,
 * rather than a MutationObserver. Algolia rebuilds its results on every
 * keystroke, and a persistent observer over that is a cost this does not need to
 * pay. The trade-off is that a link injected after load keeps showing the stale
 * host in the status bar until it is interacted with.
 *
 * Links only. `link[rel=canonical]`, `meta[property="og:url"]` and JSON-LD are
 * deliberately untouched — those *should* keep pointing at production, and
 * rewriting them on a development store would be an active SEO hazard.
 */
(function () {
  'use strict';

  /** Hosts that serve the production storefront. Inert when we are on one. */
  var PRODUCTION_HOSTS = ['ocs.ca', 'www.ocs.ca'];

  var SHOPIFY_SUFFIX = '.myshopify.com';

  /** Opt-out for a link that is genuinely meant to leave the current store. */
  var ATTR_KEEP = 'data-ocs-keep-origin';

  /**
   * Paths that must never gain a locale segment. CDN assets and app-proxy
   * routes are not localized pages; a `/fr` prefix 404s them.
   */
  var UNLOCALIZED_PREFIXES = ['/cdn/', '/apps/', '/services/', '/.well-known/'];

  var SELECTOR_LINK = 'a[href]';

  /* ------------------------------------------------------------------ *
   * Pure helpers. Everything worth asserting lives here.
   * ------------------------------------------------------------------ */

  function hasSuffix(value, suffix) {
    return value.length >= suffix.length && value.indexOf(suffix, value.length - suffix.length) !== -1;
  }

  function isProductionHost(host) {
    return PRODUCTION_HOSTS.indexOf(host) !== -1;
  }

  /**
   * A host we are willing to pull onto the current origin: the production
   * storefront, or any other Shopify-hosted store. Callers must already have
   * excluded the current host — this does not know what that is.
   */
  function isRewritableHost(host) {
    return isProductionHost(host) || hasSuffix(host, SHOPIFY_SUFFIX);
  }

  /**
   * Prepend the active locale segment, idempotently.
   *
   * `localeRoot` is window.Shopify.routes.root — '/' on English, '/fr/' on
   * French. Production's links carry no locale, so a French visitor following
   * one landed on the English page; re-prefixing keeps them in French instead.
   * That is a deliberate divergence from production behaviour.
   *
   * Known limitation, left explicit rather than silently patched: a *foreign*
   * locale prefix is not stripped, so `/en/pages/faq` viewed on `/fr` stays
   * `/en/...`. English is the default locale on this store and carries no
   * prefix, so that URL shape should not exist in the first place.
   *
   * @param {string} pathname
   * @param {string} localeRoot
   * @returns {string}
   */
  function applyLocale(pathname, localeRoot) {
    var prefix;
    var i;

    if (typeof pathname !== 'string' || pathname === '') return pathname;
    if (typeof localeRoot !== 'string' || localeRoot === '' || localeRoot === '/') return pathname;

    prefix = localeRoot.charAt(localeRoot.length - 1) === '/' ? localeRoot.slice(0, -1) : localeRoot;

    // Already prefixed — '/fr' itself, or anything below it.
    if (pathname === prefix) return pathname;
    if (pathname.indexOf(prefix + '/') === 0) return pathname;

    for (i = 0; i < UNLOCALIZED_PREFIXES.length; i += 1) {
      if (pathname.indexOf(UNLOCALIZED_PREFIXES[i]) === 0) return pathname;
    }

    return prefix + pathname;
  }

  /**
   * The whole decision, as one pure function.
   *
   * @param {string} href raw attribute value
   * @param {{currentOrigin: string, currentHost: string, currentProtocol: string, localeRoot: string}} context
   * @returns {string|null} the replacement URL, or null to leave the link alone
   */
  function rewriteHref(href, context) {
    var url;
    var pathname;

    if (typeof href !== 'string' || href === '') return null;
    if (!context || !context.currentHost) return null;

    try {
      url = new URL(href, context.currentOrigin + '/');
    } catch (error) {
      /* Not a URL we can reason about — leave it exactly as authored. */
      return null;
    }

    // mailto:, tel:, javascript: — nothing to re-host.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

    // Already where we want it. Relative hrefs and bare '#fragment' resolve
    // against the current origin above, so they land here and are left alone.
    if (url.host === context.currentHost) return null;

    if (!isRewritableHost(url.host)) return null;

    url.protocol = context.currentProtocol;
    url.host = context.currentHost;

    pathname = applyLocale(url.pathname, context.localeRoot);
    if (pathname !== url.pathname) url.pathname = pathname;

    return url.href;
  }

  /* ------------------------------------------------------------------ */

  /**
   * Read the current page's origin and locale once, so a sweep over hundreds
   * of anchors does not re-read them per link.
   *
   * @returns {object}
   */
  function currentContext() {
    var location = window.location || {};
    var protocol = location.protocol || 'https:';
    var host = location.host || '';
    var root = '/';

    if (window.Shopify && window.Shopify.routes && typeof window.Shopify.routes.root === 'string') {
      root = window.Shopify.routes.root;
    }

    return {
      currentOrigin: location.origin || protocol + '//' + host,
      currentHost: host,
      currentProtocol: protocol,
      localeRoot: root,
    };
  }

  /**
   * @param {Element} anchor
   * @param {object} context
   * @returns {boolean} whether the href was changed.
   */
  function rewriteElement(anchor, context) {
    var href;
    var next;

    if (!anchor || typeof anchor.getAttribute !== 'function') return false;
    if (typeof anchor.setAttribute !== 'function') return false;
    if (anchor.getAttribute(ATTR_KEEP) !== null) return false;

    href = anchor.getAttribute('href');
    next = rewriteHref(href, context);
    if (next === null || next === href) return false;

    anchor.setAttribute('href', next);
    return true;
  }

  /**
   * @param {Document|Element} scope
   * @returns {number} how many hrefs were changed.
   */
  function sweep(scope) {
    var root = scope || document;
    var links;
    var context;
    var changed = 0;
    var i;

    if (!root || typeof root.querySelectorAll !== 'function') return 0;

    links = root.querySelectorAll(SELECTOR_LINK);
    if (!links || !links.length) return 0;

    context = currentContext();
    for (i = 0; i < links.length; i += 1) {
      if (rewriteElement(links[i], context)) changed += 1;
    }

    return changed;
  }

  /**
   * Correct a link at interaction time, covering anything injected after the
   * sweep — Algolia results, mega-menu panels, FAQ load-more.
   *
   * Capture phase is required, not stylistic: faq-topic-selector.js calls
   * preventDefault() and location.assign() on its own links, so the href has to
   * be right before that handler reads it. Rewriting the attribute rather than
   * intercepting the navigation keeps the two compatible.
   *
   * @param {object} event
   * @returns {boolean} whether the href was changed.
   */
  function handleInteraction(event) {
    var target = event && event.target;
    var link;

    try {
      if (!target || typeof target.closest !== 'function') return false;

      link = target.closest(SELECTOR_LINK);
      if (!link) return false;

      return rewriteElement(link, currentContext());
    } catch (error) {
      /* This must never interrupt a navigation. */
      return false;
    }
  }

  /**
   * @param {Document|Element} scope
   * @returns {boolean} whether the module armed itself.
   */
  function init(scope) {
    var root = scope || document;
    var location = window.location || {};

    // The rule, stated literally: do nothing when we are already on production.
    if (isProductionHost(location.host)) return false;

    if (!root || typeof root.addEventListener !== 'function') return false;

    root.addEventListener('click', handleInteraction, true);
    root.addEventListener('auxclick', handleInteraction, true);
    // So that "copy link address" yields the rewritten URL too.
    root.addEventListener('contextmenu', handleInteraction, true);

    sweep(root);
    return true;
  }

  window.OCSLinkOrigin = {
    ATTRIBUTES: {
      keepOrigin: ATTR_KEEP,
    },
    applyLocale: applyLocale,
    currentContext: currentContext,
    handleInteraction: handleInteraction,
    init: init,
    isRewritableHost: isRewritableHost,
    rewriteElement: rewriteElement,
    rewriteHref: rewriteHref,
    sweep: sweep,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function bootstrap() {
      init(document);
    });
  } else {
    init(document);
  }
})();
